/**
 * THE CHANGE THAT IS WAITING, and the two ways out of it: the panel that stands where the move
 * form stands while the client has not been told.
 */
import { Link } from 'react-router-dom';
import { VISIT_CHANGE_KIND_LABELS } from '@fk/shared';
import type { ChangeRow, ContactRow, MessageIntentRow } from '../../api.ts';
import { Card, Detail, Pill } from '../../components/AppShell.tsx';
import { NotToldPill } from '../../components/StatusPills.tsx';
import { formatInstant, longDay } from '../../format.ts';
import { MESSAGE_CHANNEL_LABELS } from '../../labels.ts';
import { SilenceForm } from './ChangeForms.tsx';
import {
  heldUntil,
  messageOnItsWay,
  messageThatFailed,
  whatToDoAbout,
  whyItDidNotGo,
} from './shared.ts';

/**
 * WHAT STANDS WHERE THE MOVE FORM STANDS while a change has not reached the client.
 *
 * The server takes one change at a time and refuses the second with `change_pending`, for the
 * reason it gives in words: two records and one truth means nobody can say what she was told. The
 * form was drawn over that anyway, so every field could be filled in and every button came back
 * refused, after the sentence had been written and the day chosen.
 *
 * There are exactly two ways out and this panel is both of them. Either the message goes and the
 * phone system reports it delivered, which is what `told_at` is and what moves the visit, or
 * somebody says how she already knows, which is the form below and moves the visit now. Nothing
 * else in this product can finish a change, so nothing else is offered here.
 *
 * AND IT SAYS WHERE THE MESSAGE IS (ops #23): still with ops, with the phone system and held for
 * the texting hours when it is, or with home for an email (ops #76 O6), which home delivers from
 * home@, or ended, with the phone system's word for why, told apart as a
 * refusal of what ops sent or a message it took and could not deliver. A text ops's own go-live
 * switch held never reached the phone system, and the panel names the switch instead (ops #31). A
 * text the phone system failed because it holds her number says what to do about it, call her to
 * confirm the number, and links her page, whose thread is where Number confirmed is (ops #33).
 */
export function WaitingPanel({
  change,
  clientId,
  contacts,
  onReload,
}: {
  change: ChangeRow;
  clientId: string;
  contacts: readonly ContactRow[];
  onReload: () => void;
}) {
  const waitingIntent = messageOnItsWay(change);
  const refused = messageThatFailed(change);
  const toWhom = (contactId: string): string =>
    contacts.find((contact) => contact.id === contactId)?.displayName ?? 'them';
  const channelWord = (intent: MessageIntentRow): string =>
    MESSAGE_CHANNEL_LABELS[intent.channel].toLowerCase();
  // Who holds it once ops has handed it over: home delivers every email since ops #76 O6, and the
  // phone system every text.
  const holderOf = (intent: MessageIntentRow): string =>
    intent.channel === 'email' ? 'Home' : 'The phone system';
  // Read when the panel draws, as the rest of it is: a reload is what shows a message moving on.
  const hold = waitingIntent ? heldUntil(waitingIntent, Date.now()) : null;
  const why = refused ? whyItDidNotGo(refused) : null;
  // A number the phone system holds is released from her household's thread (ops #33).
  const todo = refused ? whatToDoAbout(refused) : null;

  return (
    <Card className="mb-6 md:mb-0 md:p-3">
      <h2 className="font-serif text-lg">Change this visit</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        A change is already on this visit and the client has not been told about it yet. Nothing
        else can change here until that one is finished, so that what they were told and what the
        calendar says can never be two different things.
      </p>

      <div className="rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-serif text-base leading-tight">
            {VISIT_CHANGE_KIND_LABELS[change.kind]}
          </h3>
          <NotToldPill />
          {change.inside24h ? <Pill tone="gold">Under a day's notice</Pill> : null}
        </div>

        <dl className="mt-1">
          <Detail label="From" value={change.fromDate ? longDay(change.fromDate) : null} />
          <Detail label="To" value={change.toDate ? longDay(change.toDate) : null} />
          <Detail label="Recorded" value={formatInstant(change.createdAt)} />
          <Detail label="Note" value={change.note} />
        </dl>

        {waitingIntent ? (
          <p className="mt-2 text-sm">
            {waitingIntent.state === 'handed_off'
              ? `${holderOf(waitingIntent)} has the ${channelWord(waitingIntent)} to ${toWhom(waitingIntent.contactId)}${
                  hold
                    ? `, and is holding it for the texting hours until ${formatInstant(hold)}`
                    : ''
                }.`
              : `The ${channelWord(waitingIntent)} to ${toWhom(waitingIntent.contactId)} is waiting to go.`}{' '}
            The visit moves the moment it is delivered, and nobody here has to do anything for that.
          </p>
        ) : refused ? (
          <p className="mt-2 text-sm">
            The message to {toWhom(refused.contactId)} did not go
            {refused.state === 'refused_by_consent'
              ? ', because they have not agreed to be messaged that way'
              : ''}
            .{why ? ` ${why}` : ''} Ring them, and then say below how they know. What they agreed to
            is recorded on their{' '}
            <Link to={`/clients/${clientId}`} className="font-medium underline">
              household page
            </Link>
            .
          </p>
        ) : (
          <p className="mt-2 text-sm">
            No message is waiting on this one, so it moves nothing until somebody says how they
            know.
          </p>
        )}

        {todo ? (
          <div className="mt-2 text-sm" data-todo="number_held">
            <p className="font-medium">{todo}</p>
            <Link
              to={`/clients/${clientId}`}
              className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
            >
              Confirm it on their page
            </Link>
          </div>
        ) : null}

        {waitingIntent ? (
          <p className="mt-2 text-sm whitespace-pre-line text-navy/75">{waitingIntent.body}</p>
        ) : null}
      </div>

      <SilenceForm changeId={change.id} onDone={onReload} />
    </Card>
  );
}
